import { Box, styled } from '@mui/material';
import { Plus } from '@styled-icons/bootstrap';
import ComponentItemTable from 'FeatureModules/ComponentChain/Components/ComponentItemsTable';
import { EComponentItemTableColumnKeys } from 'FeatureModules/ComponentChain/utils/headerColumns';
import Loader from 'Components/Forms/Loader';
import SearchToolbar from 'Components/Forms/SearchToolbar';
import ProductSummarySkeleton from 'FeatureModules/ProductPage/Components/Tables/ProductSummarySkeleton';
import { CardIllustration, EmptyStateCard, ThemeButton } from 'DesignSystem';
import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { IComponentItem } from 'FeatureModules/ComponentChain/types/component.types';
import {
  EBackgroundTypes,
  EIllustrationTypes,
  EItemTypes,
} from 'DesignSystem/types/background.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import useComponentItems from '../hooks/useComponentItems';
import useComponentItemActions from '../hooks/useComponentItemActions';
import ActionContainer from 'Components/ActionContainer';
import EmptySearchResults from 'Components/EmptySearchResults';
import ErrorState from 'Components/ErrorState';
import PageContainer from 'Components/PageContainer';
import Filter from 'Components/Filter/Filter';
import InfiniteScrollWrapper from 'Components/InfiniteScrollWrapper';

const Container = styled('div')(() => ({
  position: 'relative',
  minHeight: 50,

  '& .MuiGrid-item': {
    display: 'flex',
  },
}));

const ComponentItems: FC = () => {
  const {
    components,
    error,
    loading,
    searchTerm,
    filters,
    hasNextPage,
    handlePageEndReached,
    handleSearchTermChange,
    handleFilterReset,
    handleFilterChange,
  } = useComponentItems();
  const navigate = useNavigate();

  const { handleCreateComponent } = useComponentItemActions();

  const handleRowClick = ({ id }: IComponentItem) => navigate(`/components/${id}`);

  if (error) return <ErrorState />;

  if (
    !components.length &&
    !searchTerm?.length &&
    !filters.some(({ value }) => !!value) &&
    !loading
  ) {
    return (
      <Box mt={4}>
        <EmptyStateCard
          title="Add your product components to start tracing the material flow of your supply chains"
          onCreateItemClick={handleCreateComponent}
          variant={EItemTypes.COMPONENT}
          buttonText="Add component"
          background={EBackgroundTypes.WORLD}
          renderIllustration={
            <CardIllustration image={EIllustrationTypes.LOCATIONS} width={300} right={25} top={0} />
          }
          size={EAvailableSizes.LARGE}
          data-testid="components-getting-started-card"
          data-testid-button="components-getting-started-card-button"
        />
      </Box>
    );
  }

  return (
    <PageContainer>
      <Filter
        filters={filters}
        onFilterChange={handleFilterChange}
        onFilterReset={handleFilterReset}
      />

      <Container>
        <ActionContainer>
          <SearchToolbar
            setSearchTerm={handleSearchTermChange}
            searchTerm={searchTerm || ''}
            className="search-input"
            data-testid="components-search-input"
            placeholder="Search component"
            actions={
              <Box ml={1}>
                <ThemeButton
                  size="medium"
                  color="PRIMARY"
                  onClick={handleCreateComponent}
                  startIcon={<Plus size={20} color="white" />}
                  data-testid="create-component-btn"
                >
                  Add component
                </ThemeButton>
              </Box>
            }
          />
        </ActionContainer>

        {loading && <Loader relativeHeightToScreen />}

        {!loading && !!components?.length && (
          <InfiniteScrollWrapper hasMore={hasNextPage} next={handlePageEndReached}>
            <ComponentItemTable
              data-testid="component-items-table"
              components={components}
              columns={[
                EComponentItemTableColumnKeys.TITLE,
                EComponentItemTableColumnKeys.PARTNER,
                EComponentItemTableColumnKeys.MODIFIED_TIMESTAMP,
                EComponentItemTableColumnKeys.ACTIONS,
              ]}
              onSelect={handleRowClick}
            />
          </InfiniteScrollWrapper>
        )}
      </Container>

      {/* No result container */}
      {!loading && !components?.length && (
        <EmptySearchResults
          message="No search results found"
          action={{
            label: 'Add component',
            onClick: handleCreateComponent,
          }}
          skeletonComponent={<ProductSummarySkeleton title={searchTerm} />}
        />
      )}
    </PageContainer>
  );
};

export default ComponentItems;
